import React, { useRef, useEffect, useState } from 'react';
import { HiOutlineChevronRight, HiOutlineChevronLeft, HiOutlineTrash, HiPaperAirplane, HiMicrophone } from 'react-icons/hi';
import ChatBubble from './ChatBubble';
import TypingIndicator from './TypingIndicator';
import SuggestedQueries from './SuggestedQueries';
import './AICopilotPanel.css';

const AICopilotPanel = ({ 
  isExpanded, 
  onToggleExpand, 
  messages, 
  loading, 
  suggestedQueries, 
  onSendMessage, 
  onRetry,
  onPin,
  pinnedChartIds,
  onClearChat,
  onActionClick
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const chatEndRef = useRef(null);

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = SpeechRecognition ? new SpeechRecognition() : null;

  if (recognition) {
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputValue(prev => prev + (prev.length > 0 ? ' ' : '') + transcript);
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };
  }

  const toggleListen = () => {
    if (isListening && recognition) {
      recognition.stop();
      setIsListening(false);
    } else {
      if (recognition) {
        recognition.start();
        setIsListening(true);
      } else {
        alert("Microphone is not supported in this browser.");
      }
    }
  };
  
  // Auto-scroll to bottom
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !loading) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleQuerySelect = (query) => {
    if (!loading) {
      onSendMessage(query);
    }
  };

  return (
    <div className={`ai-copilot-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="copilot-header">
        <button className="collapse-btn" onClick={onToggleExpand}>
          {isExpanded ? <HiOutlineChevronRight /> : <HiOutlineChevronLeft />}
        </button>
        {isExpanded && (
          <>
            <span className="copilot-title">
              <span className="ai-badge">🤖</span>
              <span className="status-dot"></span>
              MaX Elite AI
            </span>
            <button className="clear-btn" onClick={onClearChat} title="Clear Chat">
              <HiOutlineTrash />
            </button>
          </>
        )}
      </div>

      {isExpanded && (
        <>
          <div className="chat-history custom-scrollbar">
            {messages.length === 0 ? (
              <div className="welcome-message">
                <div className="welcome-icon">🤖</div>
                <h3>Hi! I'm MaX AI</h3>
                <p>Your intelligent industrial copilot. I can analyze OEE, predict downtimes, and manage your maintenance workflows.</p>
                <div className="example-queries-hint">
                  <span>How can I help you today?</span>
                </div>
              </div>
            ) : (
              messages.map(msg => (
                <ChatBubble 
                  key={msg.id} 
                  message={msg} 
                  onRetry={msg.isError ? onRetry : undefined}
                  onPin={onPin}
                  isPinned={pinnedChartIds.includes(msg.id)}
                  onActionClick={onActionClick}
                />
              ))
            )}
            
            {loading && (
              <div className="chat-bubble-container ai">
                <div className="chat-avatar">🤖</div>
                <div className="chat-bubble-content">
                  <div className="chat-bubble ai-bubble">
                    <TypingIndicator />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="chat-input-area">
            <SuggestedQueries queries={suggestedQueries} onSelect={handleQuerySelect} />
            <form className="input-form" onSubmit={handleSubmit}>
              <input 
                type="text" 
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder={isListening ? "Listening..." : "Ask MaX AI..."}
                disabled={loading}
              />
              <button 
                type="button" 
                onClick={toggleListen}
                className={`voice-btn ${isListening ? 'listening' : ''}`}
                title="Speak to AI"
              >
                <HiMicrophone />
              </button>
              <button type="submit" disabled={!inputValue.trim() || loading} className="send-btn">
                <HiPaperAirplane style={{ transform: 'rotate(90deg)' }} />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default AICopilotPanel;
